import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { track } from '@theme/r-analytics';

/** The section that renders the no-query state (sections/predictive-search-empty.liquid). */
const EMPTY_SECTION_ID = 'predictive-search-empty';

/** @type {Promise<typeof import('@theme/r-search-synonyms')> | undefined} */
let searchSynonyms;

/**
 * WS26 — the search vocabulary (`1L` → `1 litre`) loads on intent: the modal ships on
 * every page, the vocabulary is only needed once someone searches. Loading it also arms the
 * submit rewrite for the modal's "View all".
 */
function loadSearchSynonyms() {
  searchSynonyms ??= import('@theme/r-search-synonyms').then((module) => {
    module.installSearchSubmitRewrite();
    return module;
  });
  return searchSynonyms;
}

/**
 * WS26 — the results echo the query they were rendered for (status line, "No results
 * for …"), which is the canonical one; put the shopper's own words back into those spans.
 * @param {string} markup
 * @param {string} typed
 */
function withTypedTerms(markup, typed) {
  const escaped = typed.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  return markup.replace(/(<span data-r-typed-terms[^>]*>)[\s\S]*?(<\/span>)/g, (_, open, close) => open + escaped + close);
}

/** What opens the modal, for the idle-state warm-up: the header's `#search-modal/showDialog` buttons. */
const IDLE_WARM_TRIGGERS = '[on\\:click^="#search-modal/"], [aria-controls="search-modal"]';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** WS20 P12: the idle-state fetch in flight, shared by the open and the warm-up. */
  #idleLoad = /** @type {Promise<void> | null} */ (null);

  /** Set after a failed idle-state fetch, so the retry skips the cached failure. */
  #idleRetry = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    // WS26 — fetch the search vocabulary the moment the modal's input takes focus, so it is
    // in hand before the first suggestion request and before "View all" can submit.
    this.addEventListener('focusin', () => loadSearchSynonyms(), { once: true, signal });

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // r: `explore` = the no-query browse feed, `query` = predictive results.
    // Drives the modal height (search-modal.liquid) and <r-search-explore>.
    this.dataset.searchState = this.refs.searchInput.value.length > 0 ? 'query' : 'explore';

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#loadIdleState, { signal });

      for (const trigger of document.querySelectorAll(IDLE_WARM_TRIGGERS)) {
        for (const type of ['pointerenter', 'focusin', 'touchstart']) {
          trigger.addEventListener(type, this.#warmIdleState, { signal, passive: true });
        }
      }

      this.addEventListener('click', this.#handleModalClick, { signal });

      // Opened before this module ran (dialog.js upgrades first): that DialogOpenEvent
      // came and went, so load the idle state for the open already in progress.
      if (dialog.refs?.dialog?.open) this.#loadIdleState();
    }

    // WS23 — result clicks publish rocky_search_click. Delegated because the results are
    // re-rendered by morph and the explore feed appends cards at runtime.
    this.addEventListener('click', this.#trackResultClick, { capture: true, passive: true, signal });

    // r: upstream re-renders the inline empty state at idle to prepend recently viewed
    // products. With the WS20 P12 skeleton there is no inline state yet, and the first open
    // renders it with them, so skip it — otherwise every visitor who has viewed a product
    // renders the explore feed and "Buy it again" on every page anyway.
    if (RecentlyViewed.getProducts().length > 0 && !this.#idleDeferred) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Publishes which result was chosen: type by the list the card sits in, position by its
   * index within that list, query from the input.
   * @param {MouseEvent} event
   */
  #trackResultClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (!link || !this.contains(link)) return;

    const card = /** @type {HTMLElement | null} */ (
      link.closest('.predictive-search-results__card, .predictive-search-results__card--query')
    );
    if (!card) return;

    let type;
    if (card.hasAttribute('data-r-explore-item')) {
      type = card.dataset.rSource === 'recs' ? 'recommendation' : 'explore';
    } else if (card.closest('[data-r-search-rail]')) {
      type = 'buy_again';
    } else if (card.classList.contains('predictive-search-results__card--query')) {
      type = 'predictive_query';
    } else if (card.classList.contains('predictive-search-results__card--product')) {
      type = 'predictive_product';
    } else {
      const heading = card.closest('slideshow-component')?.querySelector('[id^="predictive-search-"]');
      type = `predictive_${heading?.id.replace('predictive-search-', '') || 'resource'}`;
    }

    const siblings = card.parentElement ? [...card.parentElement.children] : [card];
    track('search_click', {
      type,
      query: this.refs.searchInput?.value.trim(),
      product_id: card.dataset.productId,
      // WS26: a split row stands for one size of the product.
      variant_id: card.dataset.variantId,
      position: siblings.indexOf(card) + 1,
      url: link.getAttribute('href') ?? undefined,
    });
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /* ↗ .cursor/references/code-notes/predictive-search.js.md#§7 */
  get #idleDeferred() {
    return !!this.refs.predictiveSearchResults.querySelector('[data-r-search-skeleton]');
  }

  #warmIdleState = () => {
    if (!this.#idleDeferred || this.#idleLoad || this.#idleRetry) return;
    sectionRenderer.getSectionHTML(EMPTY_SECTION_ID, true, this.#emptyStateUrl()).catch(() => {});
  };

  /** Whether a query has taken over the results (typed text, or its results on screen). */
  get #querying() {
    return this.refs.searchInput.value.length > 0 || this.dataset.searchState === 'query';
  }

  #loadIdleState = () => {
    if (!this.#idleDeferred || this.#idleLoad) return;

    const skeleton = this.refs.predictiveSearchResults.querySelector('[data-r-search-skeleton]');
    if (skeleton instanceof HTMLElement) skeleton.hidden = false;

    this.#idleLoad = this.#resetSearch(!this.#idleRetry)
      .catch((error) => {
        // Retry once, uncached — unless the shopper has started a query meanwhile. The
        // retry would clear their input and morph the feed over their results; the next
        // clear or close reloads the idle state anyway.
        if (this.#querying) return;
        throw error;
      })
      .catch(() => this.#resetSearch(false))
      .then(() => {
        this.#idleRetry = false;
      })
      .catch(() => {
        // The search box works without the feed. Stop the placeholder shimmering and
        // try again, uncached, on the next open.
        this.#idleRetry = true;
        if (skeleton instanceof HTMLElement && skeleton.isConnected) {
          skeleton.hidden = true;
          skeleton.closest('[aria-busy]')?.removeAttribute('aria-busy');
        }
      })
      .finally(() => {
        this.#idleLoad = null;
      });
  };

  /* ↗ .cursor/references/code-notes/predictive-search.js.md#§1 */
  #handleDialogClose = () => {
    // r: nothing awaits a close's reset; a failed fetch here is retried by the next open.
    this.#resetSearch().catch(() => {});
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;

    // ↗ .cursor/references/code-notes/predictive-search.js.md#§2
    if (this.dialog?.refs.dialog.open) return;

    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    /* ↗ .cursor/references/code-notes/predictive-search.js.md#§6 */
    if (event.key === 'Enter') {
      event.preventDefault();

      const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
        window.location.href = singleResultContainer.dataset.singleResultUrl;
        return;
      }

      if (this.#currentIndex >= 0) this.#currentItem?.querySelector('a')?.click();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    // ↗ .cursor/references/code-notes/predictive-search.js.md#§3
    const isExplore = this.dataset.searchState === 'explore';
    const next = currentIndex < totalItems - 1 ? currentIndex + 1 : isExplore ? currentIndex : 0;
    const previous = currentIndex > 0 ? currentIndex - 1 : isExplore ? currentIndex : totalItems - 1;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = next;
        break;

      case 'Tab':
        if (isExplore) break;

        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey ? previous : next;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = previous;
        break;

      case 'Home':
        if (!isExplore) break;
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = 0;
        break;

      case 'End':
        if (!isExplore) break;
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = totalItems - 1;
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /* ↗ .cursor/references/code-notes/predictive-search.js.md#§4 */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.dataset.searchState = 'query';
    this.#getSearchResults(searchTerm);
  }, 200);

  /* ↗ .cursor/references/code-notes/predictive-search.js.md#§5 */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    // WS26 — the request carries the query in Rocky's vocabulary (`1L` → `1 litre`); the
    // input keeps what the shopper typed, and the echoes in the results are swapped back to it.
    // The controller is claimed before the import is awaited, so a reset or a newer query
    // that lands while the vocabulary is still loading aborts this one. A failed import
    // falls back to the shopper's own words rather than breaking search.
    const abortController = this.#createAbortController();
    const query = await loadSearchSynonyms().then(
      (module) => module.canonicalQuery(searchTerm),
      () => searchTerm
    );
    if (abortController.signal.aborted) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        // Before the morph, so the status region is announced once, with the shopper's words.
        if (query !== searchTerm) resultsMarkup = withTypedTerms(resultsMarkup, searchTerm);

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /** The URL the empty state is rendered against: this page, minus pagination. */
  #emptyStateUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return url;
  }

  #resetSearch = async (useCache = false) => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = this.#emptyStateUrl();

    let emptySectionMarkup;
    try {
      emptySectionMarkup = await sectionRenderer.getSectionHTML(EMPTY_SECTION_ID, useCache, url);
    } catch (error) {
      // r: superseded (a query started, or another reset) — its failure is not ours to report.
      if (abortController.signal.aborted) return;
      throw error;
    }
    if (abortController.signal.aborted) return;

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      // r: every bail-out below skips the recently-viewed prepend only. It must never
      // skip the morph, or clearing/closing leaves stale results on screen.
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (abortController.signal.aborted) return;
      const recentlyViewedProductsHtml = recentlyViewedMarkup
        ? new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html').getElementById('predictive-search-products')
        : null;
      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

      if (recentlyViewedProductsHtml && collectionElement) {
        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.dataset.searchState = 'explore';
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
